import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { makeSpecColorRow, type SpecColorRow } from '@codezo/product-specs';

const isHex = (code: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(code.trim());

/**
 * Colourways as structured rows rather than one free-text field, so a hex or
 * Pantone reference stays machine-readable for quotes and production.
 */
export function SpecColorsEditor({
	value,
	onChange,
	disabled,
}: {
	value: SpecColorRow[];
	onChange: (next: SpecColorRow[]) => void;
	disabled?: boolean;
}) {
	const patch = (uid: string, partial: Partial<SpecColorRow>) =>
		onChange(value.map((row) => (row.uid === uid ? { ...row, ...partial } : row)));

	/* Only one colour can be the headline colour. */
	const setPrimary = (uid: string, isPrimary: boolean) =>
		onChange(value.map((row) => ({ ...row, isPrimary: isPrimary ? row.uid === uid : false })));

	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-center justify-between">
				<Label className="text-xs uppercase tracking-[0.06em] text-text-4">Colours</Label>
				<button
					type="button"
					disabled={disabled}
					onClick={() => onChange([...value, makeSpecColorRow()])}
					className="flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 text-xs text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
				>
					<Plus size={12} />
					Add colour
				</button>
			</div>

			{value.length === 0 ? (
				<p className="text-xs text-text-4">No colours specified.</p>
			) : (
				value.map((row) => (
					<div
						key={row.uid}
						className="flex flex-col gap-2 rounded-lg border border-line-1 bg-surface p-2.5"
					>
						<div className="flex items-center gap-2">
							<span
								className={cn(
									'h-6 w-6 shrink-0 rounded-md border border-line-2',
									!isHex(row.code) && 'bg-stripes-light'
								)}
								style={
									isHex(row.code)
										? { backgroundColor: row.code.trim() }
										: undefined
								}
							/>
							<Input
								value={row.name}
								onChange={(e) => patch(row.uid, { name: e.target.value })}
								placeholder="Colour name, e.g. Natural ecru"
								maxLength={60}
								disabled={disabled}
							/>
							<button
								type="button"
								disabled={disabled}
								onClick={() => onChange(value.filter((c) => c.uid !== row.uid))}
								className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-destructive disabled:cursor-default disabled:opacity-50"
								aria-label="Remove colour"
							>
								<Trash2 size={14} />
							</button>
						</div>

						<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
							<Input
								value={row.code}
								onChange={(e) => patch(row.uid, { code: e.target.value })}
								placeholder="#2E2E2E"
								maxLength={30}
								disabled={disabled}
							/>
							<Input
								value={row.pantone}
								onChange={(e) => patch(row.uid, { pantone: e.target.value })}
								placeholder="PMS 5535 C"
								maxLength={40}
								disabled={disabled}
							/>
							<Input
								value={row.appliesTo}
								onChange={(e) => patch(row.uid, { appliesTo: e.target.value })}
								placeholder="Applies to"
								maxLength={60}
								disabled={disabled}
							/>
							<Input
								value={row.finish}
								onChange={(e) => patch(row.uid, { finish: e.target.value })}
								placeholder="Finish"
								maxLength={60}
								disabled={disabled}
							/>
						</div>

						<button
							type="button"
							disabled={disabled}
							onClick={() => setPrimary(row.uid, !row.isPrimary)}
							className={cn(
								'flex w-fit cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1 text-xs transition-colors disabled:cursor-default disabled:opacity-50',
								row.isPrimary
									? 'text-accent'
									: 'text-text-4 hover:bg-surface-hover hover:text-text-2'
							)}
						>
							<span
								className={cn(
									'h-2.5 w-2.5 rounded-full border',
									row.isPrimary ? 'border-accent bg-accent' : 'border-line-3'
								)}
							/>
							Primary colour
						</button>
					</div>
				))
			)}
		</div>
	);
}
